import React, { useEffect, useMemo, useState } from 'react';
import { Calendar } from 'lucide-react';
import { MONTH_OPTIONS } from '../../constants/formOptions';

function pad2(n) {
  return String(n).padStart(2, '0');
}

function parseYmd(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? '');
  if (!match) return null;
  return { year: match[1], month: match[2], day: match[3] };
}

function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

function buildYearOptions(span = 40) {
  const current = new Date().getFullYear();
  return Array.from({ length: span + 1 }, (_, i) => String(current - span + i));
}

function formatDisplayDate(value) {
  const parsed = parseYmd(value);
  if (!parsed) return null;
  const month = MONTH_OPTIONS.find((m) => m.value === parsed.month)?.label ?? parsed.month;
  return `${month} ${Number(parsed.day)}, ${parsed.year}`;
}

function ChipRow({ options, selected, onSelect }) {
  return (
    <div className="flex flex-wrap gap-2 mb-3">
      {options.map((opt) => {
        const val = typeof opt === 'string' ? opt : opt.value;
        const label = typeof opt === 'string' ? opt : opt.label;
        const active = selected === val;
        return (
          <button
            key={val}
            type="button"
            onClick={() => onSelect(val)}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
              active
                ? 'bg-[var(--color-primary)] border-[var(--color-primary)] text-[var(--color-text-inverse)]'
                : 'bg-[var(--color-surface-muted)] border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-[var(--color-border-subtle)]'
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Date picker (matches mobile DatePickerField). Stores YYYY-MM-DD.
 */
export default function DatePickerField({
  value,
  onChange,
  placeholder = 'Select date',
  title = 'Select date',
  error = false,
}) {
  const today = new Date();
  const parsed = parseYmd(value);
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(parsed?.year ?? String(today.getFullYear()));
  const [month, setMonth] = useState(parsed?.month ?? pad2(today.getMonth() + 1));
  const [day, setDay] = useState(parsed?.day ?? pad2(today.getDate()));

  const yearOptions = useMemo(() => buildYearOptions(40), []);
  const monthChips = MONTH_OPTIONS.map((m) => ({ value: m.value, label: m.label.slice(0, 3) }));
  const dayMax = daysInMonth(Number(year), Number(month));
  const dayOptions = useMemo(
    () => Array.from({ length: dayMax }, (_, i) => pad2(i + 1)),
    [dayMax],
  );

  useEffect(() => {
    if (Number(day) > dayMax) setDay(pad2(dayMax));
  }, [day, dayMax]);

  const openPicker = () => {
    const next = parseYmd(value);
    setYear(next?.year ?? String(today.getFullYear()));
    setMonth(next?.month ?? pad2(today.getMonth() + 1));
    setDay(next?.day ?? pad2(today.getDate()));
    setOpen(true);
  };

  const display = formatDisplayDate(value);

  const triggerClass = `themed-input w-full px-4 py-3 rounded-lg text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] ${
    error ? 'border-red-500 focus:ring-red-500' : ''
  }`;

  return (
    <>
      <button type="button" onClick={openPicker} className={triggerClass}>
        <span className={display ? 'text-[var(--color-text)]' : 'text-[var(--color-text-muted)]'}>
          {display ?? placeholder}
        </span>
        <Calendar className="w-4 h-4 text-[var(--color-text-muted)] shrink-0 ml-2" />
      </button>

      {open ? (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="elevated-card w-full max-w-lg rounded-xl p-5 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-[var(--color-text)] mb-4">{title}</h3>

            <p className="text-sm font-medium text-[var(--color-text-secondary)] mb-2">Month</p>
            <ChipRow options={monthChips} selected={month} onSelect={setMonth} />

            <p className="text-sm font-medium text-[var(--color-text-secondary)] mb-2">Day</p>
            <ChipRow options={dayOptions} selected={day} onSelect={setDay} />

            <p className="text-sm font-medium text-[var(--color-text-secondary)] mb-2">Year</p>
            <ChipRow options={yearOptions} selected={year} onSelect={setYear} />

            <button
              type="button"
              onClick={() => {
                onChange(`${year}-${month}-${day}`);
                setOpen(false);
              }}
              className="brand-gradient-btn w-full mt-4 px-4 py-2.5 rounded-lg font-medium"
            >
              Use this date
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
